/**
 * Modèle d'affichage NORMALISÉ d'un objet de jeu (cf wiki §24.12).
 *
 * Le front manipule au moins deux formes d'« objet » :
 *   - `MeItemEntry`        (inventaire : riche, bonus + affixes rolled)
 *   - `ExcavationLootItem` (butin de fouille : nom / rareté / desc / qty)
 *
 * `DisplayItem` est le dénominateur commun consommé par `<ItemDisplay>` et
 * `<ItemTooltipContent>`. `toDisplayItem()` détecte la forme en entrée et
 * produit ce modèle (stats unifiées = bonus principal + affixes secondaires).
 *
 * Source de vérité de la rareté : `ITEM_RARITY_LABELS` / `ITEM_RARITY_TONE`
 * de `inventory.ts` (échelle 0..3 = Commun / Rare / Épique / Légendaire). Les
 * helpers divergents d'`archaeology.ts` (« Peu commun ») sont dépréciés au
 * profit de cette échelle unique.
 */
import type { MeItemEntry, ItemKind } from '@/types/inventory';
import {
  ITEM_RARITY_LABELS,
  ITEM_RARITY_TONE,
  ITEM_KIND_LABELS,
  formatAffixKey,
} from '@/types/inventory';
import type { ExcavationLootItem } from '@/types/archaeology';
// Sens d'une stat (« plus haut/bas = mieux ») : source de vérité UNIQUE.
import { statHigherIsBetter } from '@/utils/statDirection';

/** Une statistique unifiée (bonus principal OU affixe secondaire). */
export interface DisplayStat {
  /** Clé brute (snake_case), sert d'identité pour la comparaison. */
  key: string;
  /** Libellé humain prêt à afficher. */
  label: string;
  /** Valeur signée. */
  value: number;
  /** True si la valeur s'exprime en pourcentage (clé en `_pct`). */
  isPercent: boolean;
  /** True si « plus c'est haut, mieux c'est » (pilote la couleur des deltas). */
  higherIsBetter: boolean;
  /** Stat « principale » (bonus rolled d'un Artifact/Relic) vs affixe secondaire. */
  isPrimary: boolean;
  /** Portée du bonus principal (Relic) : planet / universe / player. */
  scope?: string | null;
  /** Borne basse de la plage de roll (null = inconnue → pas de barre). */
  min?: number | null;
  /** Borne haute de la plage de roll. Avec `min` et `max > min`, le tooltip
   *  affiche une barre « où se situe la valeur dans sa plage ». */
  max?: number | null;
}

/** Modèle d'affichage normalisé d'un objet, indépendant de sa source. */
export interface DisplayItem {
  /** Identité stable pour l'épinglage / comparaison (itemId ou type:code). */
  id: string | number;
  code: string;
  imageCode?: string | null;
  /** Dossier d'image (kind) ; défaut 'items'. Permet de réutiliser la carte
   *  pour d'autres familles (ex. 'ship_components'). */
  imageFolder?: string | null;
  name: string;
  description?: string;
  kind?: ItemKind | null;
  /** Libellé de TYPE pour la languette de carte (Relique / Artifact / Moteur…).
   *  Items : libellé du kind ; composants : catégorie. Optionnel. */
  typeLabel?: string;
  /** Rareté 0..3 (cf ITEM_RARITY_*). */
  rarity: number;
  /** Set de composants (qualité orthogonale) : si défini, contour VERT
   *  prioritaire sur la couleur de rareté. */
  setId?: number | null;
  /** COMPOSANT : niveau d'augmentation (atelier station) ; 0/absent = non augmenté.
   *  Rendu en étoiles de forge ★ sur la carte. */
  augmentationLevel?: number | null;
  /** COMPOSANT : true = type BASIQUE (IsFixed = équipement de départ, stats au
   *  plancher de qualité). Rendu en badge « Basique » (le distingue des pièces
   *  lootées de même rareté Commun). Cf isBasicComponentCode. */
  isBasic?: boolean | null;
  quantity?: number;
  isTradable?: boolean;
  /** COMPOSANT : taille de slot 1..4 (1=petit … 4=capital) rendue en symboles. */
  slotSize?: number | null;
  /** COMPOSANT : carburant / élément requis (libellé). */
  fuelLabel?: string | null;
  /** COMPOSANT : code ressource carburant/élément (image + couleur de l'hexagone). */
  fuelCode?: string | null;
  origin?: string | null;
  acquiredAt?: string | null;
  requiredGlyphId?: number | null;
  /** Stats unifiées (bonus principal + affixes), triées primaire d'abord. */
  stats: DisplayStat[];
}

// Sens d'une stat (« plus haut = mieux » vs « plus bas = mieux ») : importé
// depuis utils/statDirection (source de vérité unique). Cf makeDisplayStat.

/** Construit un DisplayStat à partir d'une clé/valeur brute. Les bornes
 *  `min`/`max` (plage de roll) alimentent la barre du tooltip. */
export function makeDisplayStat(
  key: string,
  value: number,
  isPrimary: boolean,
  scope?: string | null,
  min?: number | null,
  max?: number | null,
): DisplayStat {
  return {
    key,
    label: formatAffixKey(key),
    value,
    isPercent: key.endsWith('_pct'),
    higherIsBetter: statHigherIsBetter(key),
    isPrimary,
    scope: scope ?? null,
    min: min ?? null,
    max: max ?? null,
  };
}

/**
 * Texte affichable d'une stat. Le pourcentage est porté par `isPercent`
 * (drapeau du backend), PAS par le suffixe « _pct » de la clé : les stats de
 * composant ont une clé humaine (« Précision ») sans suffixe.
 */
export function formatStat(stat: DisplayStat): string {
  const sign = stat.value > 0 ? '+' : '';
  return stat.isPercent ? `${sign}${stat.value}%` : `${sign}${stat.value}`;
}

/** Libellé de rareté canonique. */
export function rarityLabel(rarity: number): string {
  return ITEM_RARITY_LABELS[rarity] ?? `T${rarity}`;
}

/** Couleur de tag Element Plus canonique pour la rareté. */
export function rarityTone(rarity: number): 'info' | 'success' | 'warning' | 'danger' {
  return ITEM_RARITY_TONE[rarity] ?? 'info';
}

// --------------------------------------------------------------------------
// Détection de forme + adaptateurs
// --------------------------------------------------------------------------

function isMeItemEntry(x: unknown): x is MeItemEntry {
  return !!x && typeof x === 'object' && 'itemId' in x && 'kind' in x && 'label' in x;
}

function isLootItem(x: unknown): x is ExcavationLootItem {
  return !!x && typeof x === 'object' && 'itemTypeId' in x && 'name' in x && !('label' in x);
}

function isDisplayItem(x: unknown): x is DisplayItem {
  return !!x && typeof x === 'object' && 'stats' in x && Array.isArray((x as DisplayItem).stats);
}

function fromMeItem(it: MeItemEntry): DisplayItem {
  const stats: DisplayStat[] = [];
  if (it.bonusCode) stats.push(makeDisplayStat(it.bonusCode, it.bonusValue, true, it.bonusScope, it.bonusMin, it.bonusMax));
  for (const a of it.affixes ?? []) stats.push(makeDisplayStat(a.key, a.value, false, null, a.min, a.max));
  // Document : révéler la recherche débloquée dans la description.
  const description = it.kind === 'Document' && it.targetResearchLabel
    ? `${it.description ? `${it.description}\n` : ''}🔓 Débloque : ${it.targetResearchLabel}`
    : it.description;
  return {
    id: it.itemId,
    code: it.code,
    imageCode: it.imageCode,
    // Nom GÉNÉRÉ prioritaire (items procéduraux « Crâne Vif de Fureur ») ;
    // sinon le label de la base (objets fixes/legacy).
    name: it.name || it.label,
    description,
    kind: it.kind,
    typeLabel: it.kind ? ITEM_KIND_LABELS[it.kind] : undefined,
    rarity: it.rarity,
    setId: it.setId ?? null,   // set d'objets (reliques) -> encart Set du tooltip
    // Taille au musée = emplacements consommés (artefacts/reliques uniquement) ->
    // hexagones de taille du tooltip/carte, unifiés avec les composants de vaisseau.
    slotSize: (it.kind === 'Artifact' || it.kind === 'Relic') ? (it.museumSize ?? null) : null,
    quantity: it.quantity,
    isTradable: it.isTradable,
    origin: it.origin,
    acquiredAt: it.acquiredAt,
    requiredGlyphId: it.requiredGlyphId,
    stats,
  };
}

function fromLootItem(it: ExcavationLootItem): DisplayItem {
  return {
    id: `loot:${it.itemTypeId}`,
    code: it.code,
    imageCode: it.imageCode,
    name: it.name,
    description: it.description,
    kind: null,
    rarity: it.rarity,
    quantity: it.quantity,
    stats: [],
  };
}

/**
 * Normalise une entrée hétérogène en `DisplayItem`. Accepte un `MeItemEntry`,
 * un `ExcavationLootItem` ou un `DisplayItem` déjà construit (idempotent).
 */
export function toDisplayItem(input: MeItemEntry | ExcavationLootItem | DisplayItem): DisplayItem {
  if (isDisplayItem(input)) return input;
  if (isMeItemEntry(input)) return fromMeItem(input);
  if (isLootItem(input)) return fromLootItem(input);
  // Repli défensif : forme inconnue -> carte minimale.
  const x = input as Partial<DisplayItem> & { code?: string };
  return {
    id: x.id ?? x.code ?? 'unknown',
    code: x.code ?? '',
    name: x.name ?? x.code ?? 'Objet',
    rarity: x.rarity ?? 0,
    stats: [],
  };
}
